import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

import { Dialog, DialogContent } from '../primitives/Dialog';
import { Input } from '../forms/Input';
import { Label } from '../forms/Label';
import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  confirmLabel: ReactNode;
  confirmVariant?: 'primary' | 'danger';
  cancelLabel?: ReactNode;
  onConfirm: () => Promise<void> | void;
  confirmDisabled?: boolean;
  isConfirming?: boolean;
  typedConfirmation?: {
    label: ReactNode;
    placeholder?: string;
    value: string;
    inputValue: string;
    onInputValueChange: (next: string) => void;
  };
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  confirmLabel,
  confirmVariant = 'primary',
  cancelLabel,
  onConfirm,
  confirmDisabled,
  isConfirming,
  typedConfirmation,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const resolvedCancel = cancelLabel ?? t('actions.cancel');
  const typedOk = typedConfirmation
    ? typedConfirmation.inputValue === typedConfirmation.value
    : true;
  const disabled = Boolean(confirmDisabled || isConfirming || !typedOk);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'w-[calc(100vw-2rem)] max-w-[520px] overflow-hidden rounded-md bg-surface p-0 shadow-lg',
          'border border-border-strong',
        )}
      >
        <div className="flex flex-col items-center px-6 pt-5 text-center">
          {icon ? <div className="pb-3">{icon}</div> : null}
          <div className="pb-1.5 text-lg font-semibold tracking-tight text-fg">{title}</div>
          {description ? <div className="text-sm text-fg-muted">{description}</div> : null}
        </div>

        {typedConfirmation ? (
          <div className="flex flex-col gap-2 px-6 pb-2 pt-4">
            <Label className="text-xs font-semibold text-fg-muted">{typedConfirmation.label}</Label>
            <Input
              value={typedConfirmation.inputValue}
              onChange={(e) => typedConfirmation.onInputValueChange(e.target.value)}
              placeholder={typedConfirmation.placeholder}
            />
          </div>
        ) : null}

        <div className="flex flex-col-reverse gap-2 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={Boolean(isConfirming)}
          >
            {resolvedCancel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} disabled={disabled}>
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
